import {
  Box,
  LightMode,
  SystemStyleObject,
  Table as ChakraTable,
  TableProps,
  Tbody,
  Td,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { Row, Table } from "@tanstack/react-table";
import { Text14 } from "components/Text";
import { Fragment } from "react/jsx-runtime";
import SmallArrowPagination from "./SmallArrowPagination";
import { SkeletonRows } from "./SkeletonRow";
import TableCell, { TableHeader } from "./TableComponents";

export const defaultBaseRowTableStyles: SystemStyleObject = {
  width: "100%",
  "td:first-of-type": {
    paddingLeft: "30px",
  },
  th: {
    borderColor: "transparent",
    borderBottomColor: "chakra-border-color",
    textAlign: "center",
  },
  "th:first-of-type": {
    paddingLeft: "30px",
  },
  minWidth: { base: "400px", xl: "973px" },
};

export const defaultThStyles: SystemStyleObject = {
  background: "cdsGrey.100", // CDS Border Primary
  marginTop: 8,
};

interface SortOption {
  label: string;
  value: string;
}

interface BaseTableProps<T> extends TableProps {
  table: Table<T>;
  sx?: SystemStyleObject;
  textWrap?: boolean;
  isLoading?: boolean;
  pageSizeOptions?: number[];
  emptyStateComponent?: React.ReactNode;
  containerProps?: object;
  thProps?: object;
  theadProps?: object;
  useFixedColumns?: boolean;
  containerSx?: SystemStyleObject;
  sortOptions?: SortOption[];
  currentSort?: string;
  defaultSortString?: string;
  onSortChange?: (value: string) => void;
}

function computeRowSpans<T>(
  rows: Row<T>[],
  getKey: (row: Row<T>) => string,
): Map<string, number> {
  // Map row.id -> rowspan (0 means "don't render")
  const spanMap = new Map<string, number>();
  let currentIndex = 0;

  while (currentIndex < rows.length) {
    const currentKey = getKey(rows[currentIndex]);

    // Find the next index where the key changes
    const nextGroupIndex = rows.findIndex(
      (row, idx) => idx > currentIndex && getKey(row) !== currentKey,
    );

    // If no change found, group extends to end of array
    const groupEnd = nextGroupIndex === -1 ? rows.length : nextGroupIndex;
    const groupSize = groupEnd - currentIndex;

    // Set rowspan for first row in group
    spanMap.set(rows[currentIndex].id, groupSize);

    // Set rowspan to 0 for remaining rows in group (don't render)
    rows
      .slice(currentIndex + 1, groupEnd)
      .forEach((row) => spanMap.set(row.id, 0));

    currentIndex = groupEnd;
  }

  return spanMap;
}

export default function BaseRowTable({
  table,
  sx = {},
  textWrap = false,
  isLoading = false,
  pageSizeOptions = undefined,
  emptyStateComponent = null,
  containerProps = {},
  thProps = {},
  theadProps = {},
  containerSx = {},
  useFixedColumns = false,
  sortOptions,
  currentSort,
  defaultSortString,
  onSortChange,
  ...props
}: BaseTableProps<any>) {
  const mergeSx = useFixedColumns ? { tableLayout: "fixed", width: "unset" } : { tableLayout: "auto" };

  const columnNumber = table._getColumnDefs().length;
  const { rows } = table.getRowModel();
  const hasSectionColumn = table.getAllColumns().some((col) => col.id === "__section");
  const spanMap = hasSectionColumn ? computeRowSpans(rows, (r) => r.getValue<string>("__section")) : new Map<string, number>();

  const tableContent = (
    <Box overflowY="scroll" overflowX="auto">
      <ChakraTable borderRadius="5px" variant="simple" sx={{ background: "transparent", ...mergeSx, ...sx }} {...props}>
        <Thead {...theadProps}>
          <LightMode>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.filter((h) => h.column.id !== "__section").map((header) => (
                  <TableHeader
                    key={header?.column?.id}
                    header={header}
                    sx={defaultThStyles}
                    {...thProps}
                  />
                ))}
              </Tr>
            ))}
          </LightMode>
        </Thead>
        <Tbody {...(isLoading ? { pt: "4" } : {})}>
          {isLoading ? (
            <SkeletonRows columns={columnNumber} rows={5} speed={1.75} />
          ) : !rows?.length && emptyStateComponent ? (
            <Tr>
              <Td colSpan={columnNumber} textAlign="center" borderBottom="none">
                {emptyStateComponent}
              </Td>
            </Tr>
          ) : (
            rows.map((row, index, rows) => {
              const sectionSpan = spanMap.get(row.id) ?? 1;
              return (
                <Fragment key={row.id}>
                  {sectionSpan > 0 && hasSectionColumn && row.getValue<string>("__section") && (
                    <Tr>
                      <Td colSpan={columnNumber} sx={{ borderBottom: "none" }}>
                        <Box width="fit-content">
                          <Text14 fontWeight="bold" wordBreak="break-word">
                            {row.getValue("__section")}
                          </Text14>
                        </Box>
                      </Td>
                    </Tr>
                  )}
                  <Tr key={row.id}>
                    {row.getVisibleCells().filter((c) => c.column.id !== "__section").map((cell) => (
                      <TableCell
                        key={cell?.id}
                        cell={cell}
                        textWrap={textWrap || false}
                        sx={index === rows.length - 1 ? { borderBottom: "none" } : undefined}
                      />
                    ))}
                  </Tr>
                </Fragment>
              );
            }))}
        </Tbody>
      </ChakraTable>
    </Box>
  );

  const topPagination = (
    <SmallArrowPagination
      pageSizeOptions={pageSizeOptions}
      table={table}
      sortOptions={sortOptions}
      currentSort={currentSort}
      defaultSortString={defaultSortString}
      onSortChange={onSortChange}
      placement="top"
    />
  );

  const bottomPagination = (
    <SmallArrowPagination
      pageSizeOptions={pageSizeOptions}
      table={table}
      sortOptions={sortOptions}
      currentSort={currentSort}
      defaultSortString={defaultSortString}
      onSortChange={onSortChange}
      placement="bottom"
    />
  );

  return (
    <Box sx={{ ...defaultBaseRowTableStyles, ...containerSx }} width="100%">
      {topPagination}
      <Box {...containerProps}>{tableContent}</Box>
      {bottomPagination}
    </Box>
  );
}
